import { Dispatch, RefObject, SetStateAction } from 'react';
import { createPopper, Instance, Placement } from '@popperjs/core';
import flip from '@popperjs/core/lib/modifiers/flip';
import { Options } from '@popperjs/core/lib/modifiers/offset';

// ---

const ARROW_OFFSET = 16; // Смещение стрелки (из центра наружу), чтоб она выпирала, а не находилась внутри поппера

const ARROW_INDENT_START = -20; // Отступ стрелки от левого края поппера (e.g. padding-left)
const ARROW_INDENT_END = 20; // Отступ стрелки от правого края поппера (e.g. padding-right)

type OffsetCallbackType = Options['offset'];

export const offsetCallback: OffsetCallbackType = ({ placement }) => {
  if (placement === 'top-start' || placement === 'bottom-start') {
    return [ARROW_INDENT_START, ARROW_OFFSET];
  }
  else if (placement === 'top-end' || placement === 'bottom-end') {
    return [ARROW_INDENT_END, ARROW_OFFSET];
  }

  return [0, ARROW_OFFSET];
};

// ---

export const FALLBACK_PLACEMENT: Placement[] = [
  'top', 'top-end', 'top-start', 'left',
  'bottom', 'bottom-start', 'bottom-end', 'right',
];

export const ANIMATION_DURATION = 300;
export const HINT_DELAY_DURATION = 400;

export const ANIMATION_STYLES = {
  entering: { opacity: 1 },
  entered:  { opacity: 1 },
  exiting:  { opacity: 0 },
  exited:  { opacity: 0 },
};

// ---

type BoundaryType = RefObject<HTMLElement> | string | undefined;

export const createPopperInstance = (
  referenceElement: HTMLElement,
  popperElement: HTMLDivElement,
  placement: Placement,
  fallbackPlacements: Placement[],
  boundary: BoundaryType,
  isTooltip: boolean,
  setAlign: Dispatch<SetStateAction<'left' | 'center'>>,
): Instance => {
  let firstRender = true;
  let boundaryElement;

  if (boundary !== undefined) {
    if (typeof boundary === 'string') {
      boundaryElement = document.getElementById(boundary) || undefined;
    }
    else {
      boundaryElement = boundary?.current || undefined;
    }
  }

  return createPopper(referenceElement, popperElement, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: offsetCallback,
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements,
          boundary: boundaryElement,
        },
        fn: (values): void => {
          if (firstRender) {
            // Отрисовывает тултип только один раз, далее flip не должен менять координаты
            firstRender = false;
            flip.fn(values);

            const placement = values.state.placement;

            // По дизайну задумано, что если hint сверху или снизу, то текст будет центрирован
            if (!isTooltip) {
              if (
                placement.startsWith('top')
                || placement.startsWith('bottom')
              ) {
                setAlign('center');
              }
              else if (
                placement.startsWith('left')
                || placement.startsWith('right')
              ) {
                setAlign('left');
              }
            }
          }
        },
      },
    ],
  });
};
